import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../../../components/layout/AppLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Search, Filter, MapPin, Plus, TrendingUp, Package, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'delivery_transport', label: 'Delivery & Transport' },
  { value: 'home_services', label: 'Home Services' },
  { value: 'errands_tasks', label: 'Errands & Tasks' },
  { value: 'shopping_assistance', label: 'Shopping Assistance' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'tutoring_education', label: 'Tutoring & Education' },
  { value: 'events_entertainment', label: 'Events & Entertainment' },
  { value: 'tech_support', label: 'Tech Support' },
  { value: 'health_wellness', label: 'Health & Wellness' },
  { value: 'items_goods', label: 'Items & Goods' },
  { value: 'other', label: 'Other' }
];

export const WantsOffersBoard = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    post_type: '',
    category: '',
    location_city: '',
    search: ''
  });

  useEffect(() => {
    loadPosts();
  }, [filters]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.post_type) params.append('post_type', filters.post_type);
      if (filters.category) params.append('category', filters.category);
      if (filters.location_city) params.append('location_city', filters.location_city);
      if (filters.search) params.append('search', filters.search);

      const response = await fetch(`${API_URL}/api/wants-offers?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Failed to load posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (value) => {
    const cat = CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Wants & Offers
            </h1>
            <p className="text-muted-foreground">
              Post what you need or what you can provide
            </p>
          </div>
          <Button onClick={() => navigate('/wants-offers/post')} className="gap-2">
            <Plus className="h-4 w-4" />
            Post Want or Offer
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={filters.post_type}
                  onChange={(e) => setFilters({ ...filters, post_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">All (Wants & Offers)</option>
                  <option value="want">Wants Only</option>
                  <option value="offer">Offers Only</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Location</label>
                <Input
                  placeholder="City..."
                  value={filters.location_city}
                  onChange={(e) => setFilters({ ...filters, location_city: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ post_type: '', category: '', location_city: '', search: '' })}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Posts Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No posts found</h3>
              <p className="text-muted-foreground mb-4">
                Be the first to post a want or offer!
              </p>
              <Button onClick={() => navigate('/wants-offers/post')}>
                Create Post
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map((post) => (
              <Card
                key={post.id}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(`/wants-offers/${post.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge
                      variant={post.post_type === 'want' ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {post.post_type === 'want' ? 'WANT' : 'OFFER'}
                    </Badge>
                    {post.budget && (
                      <span className="text-sm font-semibold text-green-600">
                        {post.currency} {post.budget.toFixed(2)}
                      </span>
                    )}
                  </div>
                  <CardTitle className="text-lg line-clamp-2">{post.title}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {post.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Filter className="h-4 w-4" />
                      <span>{getCategoryLabel(post.category)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>{post.location_city}, {post.location_country}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-xs text-muted-foreground">
                        by {post.display_name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(post.created_at)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Load More */}
        {posts.length >= 50 && (
          <div className="text-center">
            <Button variant="outline" onClick={loadPosts}>
              Load More
            </Button>
          </div>
        )}
      </div>
    </AppLayout>
  );
};
